import type React from "react";

/**
 * The field styling every admin CRM drawer form uses.
 *
 * These were declared inside the event edit drawer, so the member drawer beside
 * it grew its own set — darker inputs, a different radius, a different label
 * weight and colour — and two forms opening from the same sidebar did not look
 * like the same product. Exporting one definition is what keeps them agreeing;
 * copying the values into each form is what let them drift in the first place.
 */
export const crmInputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "0.5rem",
  padding: "0.55rem 0.75rem",
  /* Bright white, so typed text reads as entered rather than as placeholder. The
   * placeholder itself is dimmed in index.css. */
  color: "#FFFFFF",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  outline: "none",
  boxSizing: "border-box",
  /* The focused field is tinted from index.css; easing it stops the change from
   * flickering as focus moves between fields. */
  transition: "border-color .18s ease, box-shadow .18s ease, background-image .18s ease",
};

export const crmLabelStyle: React.CSSProperties = {
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  fontWeight: 700,
  color: "#94A3B8",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  marginBottom: "0.35rem",
  display: "block",
};

/** A multi-line field: the same box, with room to grow and a sane resize axis. */
export const crmTextareaStyle: React.CSSProperties = {
  ...crmInputStyle,
  minHeight: "5.5rem",
  resize: "vertical",
  lineHeight: 1.55,
};

/**
 * The hint that sits under a field, explaining what to type.
 *
 * It used to be an ordinary body line in a slightly dimmer grey, at the same size
 * as the values above it and hard against them — so it read as another piece of
 * the record rather than as guidance. Italic and a size down separates it at a
 * glance without making it unreadable, and the top margin is what stops it
 * crowding the field it belongs to.
 *
 * The same hint was inline-styled four different ways across the member and
 * speaker drawers. One definition is what keeps them agreeing.
 */
export const crmHelperTextStyle: React.CSSProperties = {
  display: "block",
  marginTop: "0.4rem",
  color: "#5F6B7D",
  fontFamily: "'Inter', sans-serif",
  /* A step below the field text, so the difference is visible without dropping to
   * a size that stops being comfortable to read. */
  fontSize: "calc(var(--chi-body-compact) * 0.88)",
  fontStyle: "italic",
  lineHeight: 1.5,
};
